import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { HeaderComponent } from '@rd/core/containers/header/header.component';
import { AssetType } from '@rd/core/services/asset-service/asset.service';
import { NavLinksService } from '@rd/core/services/dropdown-link-items/nav-links.service';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { CoreState } from '@rd/core/store/reducers';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { GuardHouseLogoutCodes } from '@rd/types/guardhouse-logout-codes.type';
import { Subject, timer } from 'rxjs';
import { debounce, filter, takeUntil } from 'rxjs/operators';
import { PageUrlSegment } from '../../types';
import { getMainUrlSegment } from '../../utils/star-page-discovery.utils';

type UserAction = 'login' | 'logout' | 'enroll' | 'changePassword';

@Component({
  selector: 'star-core-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StarHeaderComponent
  extends HeaderComponent
  implements OnInit, OnDestroy
{
  @HostBinding('class.star-core-header') componentClass = true;

  destroy$ = new Subject<void>();
  public readonly AssetType = AssetType;

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  constructor(
    protected cdRef: ChangeDetectorRef,
    protected guardhouseService: GuardhouseService,
    protected navLinksService: NavLinksService,
    protected location: Location,
    protected router: Router,
    public urlUtilsService: UrlUtilsService,
    protected store: Store<CoreState>,
    private dialogService: DialogService,
    private liveAnnouncer: LiveAnnouncer,
  ) {
    super(
      cdRef,
      guardhouseService,
      navLinksService,
      location,
      router,
      urlUtilsService,
      store,
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    const urlChangedEvent$ = this.router.events
      .pipe(takeUntil(this.destroy$))
      .pipe(filter((event) => event instanceof NavigationEnd));

    urlChangedEvent$
      .pipe(
        debounce(() => {
          // navigationEnd event happens when the old route still being displayed
          // add a delay to avoid live announcer say the old route h1 content
          return timer(500);
        }),
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const mainUrlSegment = getMainUrlSegment(
            event.urlAfterRedirects,
          ) as PageUrlSegment;
          this.setLiveAnnouncer(mainUrlSegment);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  login(): void {
    this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
      id: 'auth-secure-route-dialog',
      screenMode: 'center-md',
    });
  }

  handleUserAction(action: UserAction): void {
    switch (action) {
      case 'enroll': {
        this.guardhouseService.redirectToEnrollUrl();
        break;
      }
      case 'login': {
        this.guardhouseService.redirectToLoginUrl();
        break;
      }
      case 'logout': {
        this.guardhouseService.redirectToLogoutUrl(
          GuardHouseLogoutCodes.IntentionalLogout,
        );
        break;
      }
      case 'changePassword': {
        this.guardhouseService.redirectToChangePasswordUrl();
        break;
      }
    }
  }

  private setLiveAnnouncer(mainUrlSegment: PageUrlSegment): void {
    if (mainUrlSegment === PageUrlSegment.TRANSFER_PROGRAMS) {
      const nextPageHeadingContent = document.querySelector('h1')?.textContent;
      if (nextPageHeadingContent) {
        this.liveAnnouncer.announce(nextPageHeadingContent);
      }
    }

    if (mainUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS) {
      const pageHeadingContentLabel = document.querySelector('h1')?.textContent;
      const pageHeadingContentValue = document.querySelector('h2')?.textContent;
      if (pageHeadingContentLabel && pageHeadingContentValue) {
        this.liveAnnouncer.announce(
          `${pageHeadingContentLabel}${pageHeadingContentValue} available`,
        );
      }
    }
  }
}
